import { useCallback, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import { POLICY_TYPES_FEATURES } from "constants/policy";
import useRowKeyboardNavigation from "ds/components/Table/hooks/useKeyboardNavigation/useRowKeyboardNavigation";
import TableBodyRow from "ds/components/Table/Row";
import { selectVisibleColumns } from "ds/components/Table/store/useTableStore/selectors";
import { TableSimpleStore } from "ds/components/TableSimple/types";
import { getItemId, isStickyLeft, isStickyRight } from "ds/components/TableSimple/helpers";
import useTypedContext from "hooks/useTypedContext";
import { PolicyTemplate } from "types/generated";
import { CreatePolicyFields } from "views/Account/PolicyCreateDrawer/types";
import { SubscriptionContext } from "views/Account/SubscriptionWrapper";

import { showPolicyCreateDrawer } from "../../../PolicyCreateDrawer";
import PoliciesTemplatesTableBodyPreview from "./BodyPreview";
import PoliciesTemplatesTableNameCell from "./Cells/NameCell";
import PoliciesTemplatesTableLabelsCell from "./Cells/LabelsCell";
import PoliciesTemplatesTableDescriptionCell from "./Cells/DescriptionCell";
import PoliciesTemplatesTableTypesCell from "./Cells/TypesCell";
import PoliciesTemplatesTableImportCell from "./Cells/ImportCell";
import { POLICY_TEMPLATES_COLUMN_ID } from "./constants";
import styles from "./styles.module.css";

type PoliciesTemplatesTableRowsProps = {
  data: PolicyTemplate[];
  store: TableSimpleStore;
  canManagePolicies: boolean;
  takenPolicyNames: string[];
};

const PoliciesTemplatesTableRows = ({
  data,
  store,
  canManagePolicies,
  takenPolicyNames,
}: PoliciesTemplatesTableRowsProps) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const keyboardProps = useRowKeyboardNavigation({ isGroup: false });
  const { tierFeatures } = useTypedContext(SubscriptionContext);

  const { columns, isFirstColumnPinned } = store(
    useShallow((state) => ({
      columns: selectVisibleColumns(state),
      isFirstColumnPinned: state.isFirstColumnPinned,
    }))
  );

  const toggleRow = useCallback((itemId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  }, []);

  return (
    <>
      {data.map((item) => {
        const itemId = getItemId(item, "id");
        const isExpanded = expandedRows.has(itemId);
        const requiredTierFeature = POLICY_TYPES_FEATURES[item.type];
        const isDisabled = !!requiredTierFeature && !tierFeatures.includes(requiredTierFeature);

        const handleImport = () => {
          showPolicyCreateDrawer({
            forceValidationOnOpen: true,
            autoSelectFirstType: false,
            takenPolicyNames,
            forcedType: item.type,
            templateId: item.id,
            defaultValues: {
              type: item.type,
              labels: item.labels?.map((value) => ({ value })) || [],
              name: item.name,
              description: item.description || "",
            } as Partial<CreatePolicyFields>,
          });
        };

        return [
          <TableBodyRow
            key={`${itemId}-row`}
            {...keyboardProps}
            className={isExpanded ? styles.expandedRow : undefined}
          >
            {columns.map((column, i) => {
              const cellId = `${itemId}-${column.id}`;
              const stickyLeft = isStickyLeft(i, isFirstColumnPinned);
              const stickyRight = isStickyRight(i, columns.length);

              const cellLayoutProps = {
                id: cellId,
                stickyLeft,
                stickyRight,
                isFirst: i === 0,
              };

              switch (column.id) {
                case POLICY_TEMPLATES_COLUMN_ID.NAME:
                  return (
                    <PoliciesTemplatesTableNameCell
                      key={cellId}
                      {...cellLayoutProps}
                      item={item}
                      isExpanded={isExpanded}
                      onToggle={() => toggleRow(itemId)}
                    />
                  );
                case POLICY_TEMPLATES_COLUMN_ID.LABELS:
                  return (
                    <PoliciesTemplatesTableLabelsCell
                      key={cellId}
                      {...cellLayoutProps}
                      item={item}
                    />
                  );
                case POLICY_TEMPLATES_COLUMN_ID.DESCRIPTION:
                  return (
                    <PoliciesTemplatesTableDescriptionCell
                      key={cellId}
                      {...cellLayoutProps}
                      item={item}
                    />
                  );
                case POLICY_TEMPLATES_COLUMN_ID.TYPES:
                  return (
                    <PoliciesTemplatesTableTypesCell
                      key={cellId}
                      {...cellLayoutProps}
                      item={item}
                    />
                  );
                case POLICY_TEMPLATES_COLUMN_ID.IMPORT:
                  return (
                    <PoliciesTemplatesTableImportCell
                      key={cellId}
                      {...cellLayoutProps}
                      onImport={handleImport}
                      canManagePolicies={canManagePolicies}
                      isDisabled={isDisabled}
                    />
                  );
                default:
                  return null;
              }
            })}
          </TableBodyRow>,
          isExpanded && (
            <PoliciesTemplatesTableBodyPreview
              key={`${itemId}-preview`}
              body={item.body}
              columnsCount={columns.length}
              keyboardProps={keyboardProps}
            />
          ),
        ];
      })}
    </>
  );
};

export default PoliciesTemplatesTableRows;
